import type { OrgRole } from '@/types/orgMembers';

import { PERMISSIONS, getRolePermissions } from './permissions';

export const ORG_ROLES: OrgRole[] = ['admin', 'manager', 'viewer'];

export interface OrgRoleMeta {
  label: string;
  /** Shape that tells roles apart without relying on colour. */
  icon: string;
  description: string;
  badgeClass: string;
}

export const ORG_ROLE_META: Record<OrgRole, OrgRoleMeta> = {
  admin: {
    label: 'Admin',
    icon: '◆',
    description: 'จัดการได้ทุกอย่างในองค์กร รวมถึงเชิญและเปลี่ยน role สมาชิก',
    badgeClass: 'border-primary/30 bg-primary/10 text-primary',
  },
  manager: {
    label: 'Manager',
    icon: '◇',
    description: 'จัดการกล้อง signage KYC งาน HITL และรายงาน',
    badgeClass: 'border-foreground/25 bg-background text-foreground',
  },
  viewer: {
    label: 'Viewer',
    icon: '○',
    description: 'ดูแดชบอร์ดและข้อมูลได้อย่างเดียว',
    badgeClass: 'border-border bg-muted text-muted-foreground',
  },
};

export interface OrgPermissionRow {
  label: string;
  /** A role has the row when it holds every permission listed. */
  permissions?: string[];
  /** Rules the backend enforces by role rather than by permission. */
  roles?: OrgRole[];
}

// The rows are computed from ROLE_PERMISSIONS, the same table the portal
// uses to show and hide features, so the matrix cannot drift from it.
export const ORG_PERMISSION_ROWS: OrgPermissionRow[] = [
  { label: 'ดูแดชบอร์ด', permissions: [PERMISSIONS.DASHBOARD_VIEW] },
  {
    label: 'ดูข้อมูล KYC และกล้อง',
    permissions: [PERMISSIONS.KYC_VERIFICATION_READ, PERMISSIONS.CAMERA_READ],
  },
  { label: 'ดูรายงาน', permissions: [PERMISSIONS.REPORTS_READ] },
  {
    label: 'ส่งออกรายงานและดู analytics',
    permissions: [PERMISSIONS.REPORTS_EXPORT, PERMISSIONS.ANALYTICS_VIEW],
  },
  {
    label: 'ตรวจและอนุมัติ KYC',
    permissions: [PERMISSIONS.KYC_VERIFICATION_APPROVE],
  },
  {
    label: 'จัดการกล้องและอุปกรณ์',
    permissions: [PERMISSIONS.CAMERA_WRITE, PERMISSIONS.CAMERA_CONFIGURE],
  },
  { label: 'จัดการ signage', permissions: [PERMISSIONS.SIGNAGE_MANAGE] },
  {
    label: 'งาน HITL: label และ QA',
    permissions: [PERMISSIONS.HITL_LABELING, PERMISSIONS.HITL_QA_REVIEW],
  },
  {
    label: 'จัดการ chatbot และ knowledge',
    permissions: [PERMISSIONS.CHATBOT_MANAGE],
  },
  {
    label: 'ตั้งค่าระบบและดู log',
    permissions: [
      PERMISSIONS.SYSTEM_CONFIG_WRITE,
      PERMISSIONS.SYSTEM_LOGS_READ,
    ],
  },
  { label: 'เชิญ เปลี่ยน role และลบสมาชิก', roles: ['admin'] },
];

export function roleHasRow(role: OrgRole, row: OrgPermissionRow): boolean {
  if (row.roles) return row.roles.includes(role);
  const granted = getRolePermissions(role) as string[];
  return (row.permissions ?? []).every(p => granted.includes(p));
}

export function isOrgRole(value: unknown): value is OrgRole {
  return value === 'admin' || value === 'manager' || value === 'viewer';
}
